/**
 * Date/time formatting for the operator panel.
 *
 * Deliberately hand-rolled rather than `Intl.DateTimeFormat`: the output must
 * be identical in the browser, in jsdom and in CI regardless of which ICU
 * build is present, and the panel only ever needs one format
 * (`ДД.ММ.ГГГГ ЧЧ:ММ:СС`). Everything renders in the viewer's local timezone;
 * where the strategy's own timezone matters it is displayed next to the value
 * as text (see the "следующий запуск" tile).
 */

const EM_DASH = '—';

function pad(value: number, width = 2): string {
  return String(value).padStart(width, '0');
}

function toDate(iso: string | null | undefined): Date | null {
  if (iso === null || iso === undefined || iso === '') return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** `2026-08-28T09:00:00Z` → `28.08.2026 12:00:00` (local time). */
export function formatDateTime(iso: string | null | undefined): string {
  const date = toDate(iso);
  if (date === null) return EM_DASH;
  return (
    `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}` +
    ` ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
  );
}

/** `2026-08-28T09:00:00Z` → `12:00:00` (local time). */
export function formatTime(iso: string | null | undefined): string {
  const date = toDate(iso);
  if (date === null) return EM_DASH;
  return `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

/**
 * Formats a calendar date that is already a plain `ГГГГ-ММ-ДД` string
 * (a local trading date, a backtest bound, a month bucket). No `Date` is
 * involved, so no timezone can shift it by a day.
 */
export function formatDateOnly(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') return EM_DASH;
  const match = /^(\d{4})-(\d{2})(?:-(\d{2}))?$/.exec(value);
  if (match === null) return value;
  const [, year, month, day] = match;
  if (day === undefined) return `${month}.${year}`;
  return `${day}.${month}.${year}`;
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * Human distance to a moment, e.g. `через 2 ч 15 мин` / `12 мин назад`.
 * Used for "next scheduled run" and for freshness of market data.
 */
export function formatRelative(iso: string | null | undefined, now: Date = new Date()): string {
  const date = toDate(iso);
  if (date === null) return EM_DASH;
  const deltaMs = date.getTime() - now.getTime();
  const future = deltaMs >= 0;
  const abs = Math.abs(deltaMs);

  let body: string;
  if (abs < MINUTE) {
    body = 'менее минуты';
  } else if (abs < HOUR) {
    body = `${Math.floor(abs / MINUTE)} мин`;
  } else if (abs < DAY) {
    const hours = Math.floor(abs / HOUR);
    const minutes = Math.floor((abs % HOUR) / MINUTE);
    body = minutes === 0 ? `${hours} ч` : `${hours} ч ${minutes} мин`;
  } else {
    const days = Math.floor(abs / DAY);
    const hours = Math.floor((abs % DAY) / HOUR);
    body = hours === 0 ? `${days} дн` : `${days} дн ${hours} ч`;
  }

  return future ? `через ${body}` : `${body} назад`;
}

/** Age of a timestamp in whole seconds, or `null` when unparseable. */
export function ageSeconds(iso: string | null | undefined, now: Date = new Date()): number | null {
  const date = toDate(iso);
  if (date === null) return null;
  return Math.max(0, Math.round((now.getTime() - date.getTime()) / 1000));
}

/** Today as `ГГГГ-ММ-ДД` in local time — the default upper bound for filters. */
export function todayIsoDate(now: Date = new Date()): string {
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** `daysAgo(30)` as `ГГГГ-ММ-ДД` in local time. */
export function isoDateDaysAgo(days: number, now: Date = new Date()): string {
  const shifted = new Date(now.getTime() - days * DAY);
  return todayIsoDate(shifted);
}
